import React, { useState, useEffect } from 'react';
import { Card, Input, Button, message, Space, Typography, Alert, Upload } from 'antd';
import { PlayCircleOutlined, FileTextOutlined, UploadOutlined, InboxOutlined } from '@ant-design/icons';
import AceEditor from 'react-ace';
import 'ace-builds/src-noconflict/mode-xml';
import 'ace-builds/src-noconflict/theme-github';
import axios from 'axios';

const { Title, Paragraph, Text } = Typography;
const { TextArea } = Input;
const { Dragger } = Upload;

// 倒计时组件
const CountdownMessage = ({ data, duration = 10 }) => {
  const [countdown, setCountdown] = useState(duration);

  useEffect(() => {
    const timer = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  return (
    <div className="bg-white p-4 rounded-lg shadow-sm border border-green-200">
      <div className="flex items-center gap-2 mb-3">
        <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center">
          <span className="text-green-600 text-sm">✓</span>
        </div>
        <div className="font-semibold text-green-700 text-base">流程部署成功</div>
        <div className="ml-auto text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
          {countdown}秒后自动关闭
        </div>
      </div>
      
      <div className="bg-gray-50 rounded-md p-3 space-y-2">
        <div className="grid grid-cols-1 gap-2 text-sm">
          <div className="flex justify-between">
            <span className="text-gray-600 font-medium min-w-[80px]">流程名称</span>
            <span className="text-gray-900 font-medium text-right">{data.process_definition_name}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600 font-medium min-w-[80px]">流程标识</span>
            <span className="text-blue-600 font-mono text-right">{data.process_definition_key}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600 font-medium min-w-[80px]">版本号</span>
            <span className="text-orange-600 font-semibold text-right">v{data.version}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600 font-medium min-w-[80px]">流程ID</span>
            <span className="text-gray-700 font-mono text-xs text-right break-all">{data.process_definition_id}</span>
          </div>
          <div className="flex justify-between pt-1 border-t border-gray-200">
            <span className="text-gray-600 font-medium min-w-[80px]">部署时间</span>
            <span className="text-gray-900 text-right">{new Date(data.deployed_at).toLocaleString('zh-CN')}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

// 示例BPMN XML
const EXAMPLE_BPMN = `<?xml version="1.0" encoding="UTF-8"?>
<bpmn:definitions xmlns:bpmn="http://www.omg.org/spec/BPMN/20100524/MODEL"
                  id="Definitions_1"
                  targetNamespace="http://bpmn.io/schema/bpmn">
  <bpmn:process id="simple_process" name="Simple Process" isExecutable="true">
    <bpmn:startEvent id="start" name="Start" />
    <bpmn:endEvent id="end" name="End" />
    <bpmn:sequenceFlow id="flow1" sourceRef="start" targetRef="end" />
  </bpmn:process>
</bpmn:definitions>`;

const BpmnDeploy = () => {
  const [bpmnXml, setBpmnXml] = useState(EXAMPLE_BPMN);
  const [deploymentName, setDeploymentName] = useState('Test Deployment');
  const [description, setDescription] = useState('A simple test deployment');
  const [loading, setLoading] = useState(false);

  // 加载示例BPMN
  const loadExample = () => {
    setBpmnXml(EXAMPLE_BPMN);
    message.success('示例BPMN已加载');
  };

  // 处理文件上传
  const handleFileUpload = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const content = e.target.result;
      setBpmnXml(content);
      message.success(`文件 ${file.name} 已成功加载`);
    };
    reader.onerror = () => {
      message.error('文件读取失败');
    };
    reader.readAsText(file);
    return false; // 阻止默认上传行为
  };

  // 文件上传前的验证
  const beforeUpload = (file) => {
    const isXml = file.type === 'text/xml' || file.name.toLowerCase().endsWith('.xml') || file.name.toLowerCase().endsWith('.bpmn');
    if (!isXml) {
      message.error('只能上传XML或BPMN文件！');
      return false;
    }
    const isLt10M = file.size / 1024 / 1024 < 10;
    if (!isLt10M) {
      message.error('文件大小不能超过10MB！');
      return false;
    }
    return handleFileUpload(file);
  };

  // 部署流程
  const handleDeploy = async () => {
    if (!bpmnXml.trim()) {
      message.error('请输入BPMN XML定义');
      return;
    }
    if (!deploymentName.trim()) {
      message.error('请输入部署名称');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post('http://127.0.0.1:8080/api/v1/processes/deploy', {
        bpmn_xml: bpmnXml,
        deployment_name: deploymentName,
        deployment_description: description
      }, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (response.status === 201) {
        const { data } = response.data;
        message.success({
          content: <CountdownMessage data={data} duration={10} />,
          duration: 10,
          style: {
            marginTop: '20px',
            width: '400px',
            marginLeft: 'auto',
            marginRight: 'auto',
            textAlign: 'center',
          },
        });
        console.log('部署结果:', response.data);
        
      }
    } catch (error) {
      console.error('部署错误:', error);
      message.error('部署失败: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* 页面标题 */}
      <div className="mb-6">
        <Title level={2} className="flex items-center gap-2 mb-2">
          <PlayCircleOutlined className="text-blue-500" />
          BPMN流程部署
        </Title>
        <Paragraph className="text-gray-600">
          上传并部署BPMN流程定义到流程引擎
        </Paragraph>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* BPMN XML编辑器 */}
        <div className="lg:col-span-2">
          <Card>
            <div className="flex justify-between items-center mb-4">
              <div className="flex items-center gap-2">
                <FileTextOutlined className="text-blue-500" />
                <Text strong>BPMN XML定义</Text>
              </div>
              <Space>
                <Upload
                  beforeUpload={beforeUpload}
                  showUploadList={false}
                  accept=".xml,.bpmn"
                >
                  <Button icon={<UploadOutlined />} size="small">
                    选择文件
                  </Button>
                </Upload>
                <Button onClick={loadExample} size="small">
                  加载示例
                </Button>
              </Space>
            </div>
            
            <div className="mb-4">
              <Dragger
                beforeUpload={beforeUpload}
                showUploadList={false}
                accept=".xml,.bpmn"
                className="mb-2"
              >
                <p className="ant-upload-drag-icon">
                  <InboxOutlined />
                </p>
                <p className="ant-upload-text">点击或拖拽文件到此区域上传</p>
                <p className="ant-upload-hint">
                  支持 .xml 和 .bpmn 文件格式
                </p>
              </Dragger>
              <Text type="secondary">或者直接在下方编辑器中输入/粘贴BPMN 2.0 XML定义</Text>
            </div>
            
            <AceEditor
              mode="xml"
              theme="github"
              value={bpmnXml}
              onChange={setBpmnXml}
              width="100%"
              height="400px"
              fontSize={14}
              showPrintMargin={true}
              showGutter={true}
              highlightActiveLine={true}
              setOptions={{
                enableBasicAutocompletion: true,
                enableLiveAutocompletion: true,
                enableSnippets: true,
                showLineNumbers: true,
                tabSize: 2,
                wrap: true
              }}
            />
          </Card>
        </div>

        {/* 部署配置 */}
        <div>
          <Card title="部署配置">
            <Space direction="vertical" className="w-full" size="large">
              <div>
                <Text strong className="text-red-500">* </Text>
                <Text strong>部署名称</Text>
                <Input
                  value={deploymentName}
                  onChange={(e) => setDeploymentName(e.target.value)}
                  placeholder="请输入部署名称"
                  maxLength={50}
                  showCount
                  className="mt-2"
                />
              </div>
              
              <div>
                <Text strong>部署描述</Text>
                <TextArea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="请输入部署描述（可选）"
                  maxLength={200}
                  showCount
                  rows={3}
                  className="mt-2"
                />
              </div>
              
              <div>
                <Button
                  type="primary"
                  icon={<PlayCircleOutlined />}
                  onClick={handleDeploy}
                  loading={loading}
                  size="large"
                  className="w-full"
                >
                  部署流程
                </Button>
                <Text type="secondary" className="text-xs mt-2 block">
                  部署后流程将在流程引擎中生效
                </Text>
              </div>
            </Space>
          </Card>
        </div>
      </div>

      {/* 使用说明 */}
      <Card title="使用说明" className="mt-6">
        <div className="space-y-2">
          <div>
            <Text>• </Text>
            <Text strong>BPMN XML</Text>
            <Text>: 请确保XML符合BPMN 2.0规范</Text>
          </div>
          <div>
            <Text>• </Text>
            <Text strong>流程ID</Text>
            <Text>: XML中的process id将作为流程的唯一标识</Text>
          </div>
          <div>
            <Text>• </Text>
            <Text strong>部署名称</Text>
            <Text>: 用于在流程引擎中识别此次部署</Text>
          </div>
          <div>
            <Text>• </Text>
            <Text strong>可执行性</Text>
            <Text>: 确保process元素的isExecutable属性为true</Text>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default BpmnDeploy;